import React, { Suspense } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import loadable from '@loadable/component';
import PropTypes from 'prop-types';
import paths from '../utils/paths';
import { ENABLE_H5_UPGRADE, ENABLE_H5_COMING_SOON } from 'client/settings';
import url from '../utils/url';

const ModelSwiper = loadable(() =>
  import(
    /* webpackChunkName: "ModelSwiper" */
    '../components/ModelSwiper'
  )
);
const Exterior = loadable(() =>
  import(
    /* webpackChunkName: "Exterior" */
    '../components/Exterior'
  )
);
const Interior = loadable(() =>
  import(
    /* webpackChunkName: "Interior" */
    '../components/Interior'
  )
);
const SummaryContainer = loadable(() =>
  import(
    /* webpackChunkName: "SummaryContainer" */
    '../containers/SummaryContainer'
  )
);
const Confirmation = loadable(() =>
  import(
    /* webpackChunkName: "Confirmation" */
    '../components/Confirmation'
  )
);

const DesignModelSelection = loadable(() =>
  import(
    /* webpackChunkName: "DesignModelSelection" */
    '../pages/Models'
  )
);
const ExteriorPage = loadable(() =>
  import(
    /* webpackChunkName: "ExteriorPage" */
    '../pages/Exterior'
  )
);
const WheelsPage = loadable(() =>
  import(
    /* webpackChunkName: "WheelsPage" */
    '../pages/Wheels'
  )
);
const InteriorPage = loadable(() =>
  import(
    /* webpackChunkName: "InteriorPage" */
    '../pages/Interior'
  )
);
const SummaryPage = loadable(() =>
  import(
    /* webpackChunkName: "SummaryPage" */
    '../pages/Summary'
  )
);
const SuccessConfirmation = loadable(() =>
  import(
    /* webpackChunkName: "SuccessConfirmation" */
    '../pages/Summary/SuccessConfirmation'
  )
);
const UpgradeMessage = loadable(() =>
  import(
    /* webpackChunkName: "UpgradeMessage" */
    '../components/UpgradeMessage'
  )
);

const REDIRECT_FROM = '/:region(us|cn)/:entry(configurator|design)/:year?/:model?';
const ROUTE_PATH = '/:region(us|cn)/:entry(configurator|design)/:year/:model';
const CONFIGURATOR_REDIRECT_TO = '/:region(us|cn)/configurator/2019/F91U36APALL/model';
const DESIGN_REDIRECT_TO = `/:region(us|cn)${paths.FF91_DESIGN_PREFIX}/model`;

const Pages = ({ match: { path, params }}) => {
  const isAppOnly = url.isAppOnly();
  return (
  <Switch>
    {
      (ENABLE_H5_UPGRADE || (isAppOnly && ENABLE_H5_COMING_SOON)) &&
      <Suspense fallback={<div />}>
        {
          isAppOnly ?
            <Route path={path} component={UpgradeMessage} /> :
            <Redirect to={`/${params.region}/design/`} />
        }
      </Suspense>
    }
    {
      !(ENABLE_H5_UPGRADE || (isAppOnly && ENABLE_H5_COMING_SOON)) &&
      <Suspense fallback={<div />}>
        <Route exact path={`${path}/model`} component={ModelSwiper} />
        <Route exact path={`${path}/exterior`} component={Exterior} />
        <Route exact path={`${path}/interior`} component={Interior} />
        <Route exact path={`${path}/summary`} component={SummaryContainer} />
        <Route
          exact
          path={`${path}/confirmation/:confirmationNum`}
          component={Confirmation}
        />
      </Suspense>
    }
    { !(ENABLE_H5_UPGRADE || (isAppOnly && ENABLE_H5_COMING_SOON)) && <Redirect from={path} to={CONFIGURATOR_REDIRECT_TO} /> }
  </Switch>
)}

const DesignPages = ({ match: { path } }) => (
  <Switch>
      <Route exact path={`${path}/model`} component={DesignModelSelection} />
      <Route exact path={`${path}/exterior`} component={ExteriorPage} />
      <Route exact path={`${path}/wheels`} component={WheelsPage} />
      <Route exact path={`${path}/interior`} component={InteriorPage} />
      <Route exact path={`${path}/summary`} component={SummaryPage} />
      <Route exact path={`${path}/summary/confirmation`}>
        <SummaryPage />
        <SuccessConfirmation open />
      </Route>
    <Redirect from={path} to={DESIGN_REDIRECT_TO} />
  </Switch>
);

const Routes = () => {
  const { pathname } = window.location;
  const isDesignEntry = /\/design/i.test(pathname);

  return (
    <Switch>
      {/* redirect to model page if any params are missing */}
      <Redirect
        exact
        from={REDIRECT_FROM}
        to={isDesignEntry ? DESIGN_REDIRECT_TO : CONFIGURATOR_REDIRECT_TO}
      />
      <Route path={ROUTE_PATH} component={isDesignEntry ? DesignPages : Pages} />
    </Switch>);
};

Pages.propTypes = {
  match: PropTypes.shape({
    path: PropTypes.string
  }).isRequired
};

export default Routes;
